import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Reveal, Words } from '../components/motion/Reveal';
import { CALC_COPY } from '../components/calculator/calcCopy';
import { PaymentCalculator } from '../components/calculator/PaymentCalculator';
import { WhyUs, Guides, CalcFaq, CalcCta } from '../components/calculator/CalcSections';

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
  el.setAttribute('content', content);
};

export default function LoanCalculator() {
  const { language } = useLanguage();
  const en = language === 'en';
  const c = CALC_COPY[en ? 'en' : 'fr'];

  useEffect(() => {
    document.title = c.title;
    setMeta('description', c.meta);
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: c.faq.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })) });
    document.head.appendChild(ld);
    return () => ld.remove();
  }, [c]);

  const l = c.introLinks;
  return (
    <div className="min-h-screen pt-24 md:pt-[120px] bg-white" data-testid="loan-calculator-page">
      <section className="relative overflow-hidden bg-gradient-to-br from-[#eef6f0] via-white to-[#e6f4ea] pt-10 md:pt-16 pb-16 md:pb-24">
        <div className="absolute inset-0 opacity-[0.35] pointer-events-none bg-[radial-gradient(#1a5c3b22_1px,transparent_1px)] [background-size:22px_22px]" />
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-emerald-200/40 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-6 lg:pt-10">
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-[#1a5c3b] bg-white border border-emerald-200 shadow-sm px-4 py-1.5 rounded-full mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />{c.eyebrow}
            </span>
            <Words as="h1" parts={[[c.h1a, 'text-transparent bg-clip-text bg-gradient-to-r from-[#1a5c3b] to-emerald-500'], [c.h1b]]} className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] mb-6" data-testid="calc-heading" />
            <Reveal delay={350} className="w-16 h-1 rounded-full bg-[#1a5c3b] mb-6" />
            <Reveal as="p" delay={450} className="text-lg md:text-xl font-light text-slate-500 leading-relaxed mb-5">{c.intro}</Reveal>
            <Reveal as="p" delay={600} className="text-slate-600 leading-relaxed">
              {l[0]}<Link to="/verifier-admissibilite" className="text-[#1a5c3b] font-semibold underline underline-offset-4">{l[1]}</Link>{l[2]}<Link to="/demander-un-pret-auto" className="text-[#1a5c3b] font-semibold underline underline-offset-4">{l[3]}</Link>{l[4]}
            </Reveal>
          </div>
          <Reveal delay={250} className="lg:col-span-6">
            <PaymentCalculator c={c} en={en} />
          </Reveal>
        </div>
      </section>
      <WhyUs c={c} />
      <Guides c={c} />
      <CalcFaq c={c} />
      <CalcCta c={c} />
    </div>
  );
}
